import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import AnimatedSection from './AnimatedSection';
import {
  EventAvailable as EventIcon,
  HowToReg as RegisterIcon,
  Lightbulb as IdeaIcon,
  Code as CodeIcon,
  PresentToAll as PresentIcon,
  EmojiEvents as TrophyIcon,
  Restaurant as FoodIcon,
  Hotel as AccommodationIcon
} from '@mui/icons-material';

function ScheduleTimeline() {
  const scheduleData = [
    {
      day: 'Day 1',
      date: 'December 11, 2025',
      time: '7:00 AM',
      title: 'Pre-Event Registration & Networking',
      description: 'Check-in, collect swag bags, meet your fellow hackers',
      icon: RegisterIcon,
      color: 'info'
    },
    {
      day: 'Day 1',
      date: 'December 11, 2025',
      time: '8:00 AM',
      title: 'Opening Ceremony',
      description: 'Welcome address, keynote speeches from UNDP and partners',
  icon: EventIcon,
      color: 'primary'
    },
    {
      day: 'Day 1',
      date: 'December 11, 2025',
      time: '10:00 AM',
      title: 'Team Formation & Ideation',
      description: 'Form teams, brainstorm solutions, choose your challenge',
  icon: IdeaIcon,
      color: 'secondary'
    },
    {
      day: 'Day 1',
      date: 'December 11, 2025',
      time: '12:00 PM',
      title: 'Hacking Begins!',
      description: 'Start building your solutions with mentor support',
  icon: CodeIcon,
      color: 'success'
    },
    {
      day: 'Day 1',
      date: 'December 11, 2025',
      time: '7:00 PM',
      title: 'Dinner & Networking',
      description: 'Meals provided, network with mentors and sponsors',
  icon: FoodIcon,
      color: 'warning'
    },
    {
      day: 'Day 1',
      date: 'December 11, 2025',
      time: '11:00 PM',
      title: 'Overnight Accommodation',
      description: 'Rest areas available for those working through the night',
  icon: AccommodationIcon,
      color: 'info'
    },
    {
      day: 'Day 2',
      date: 'December 12, 2025',
      time: '8:00 AM',
      title: 'Breakfast & Continued Development',
      description: 'Refuel and continue building your solution',
  icon: FoodIcon,
      color: 'warning'
    },
    {
      day: 'Day 2',
      date: 'December 12, 2025',
      time: '12:00 PM',
      title: 'Mentor Check-ins',
      description: 'One-on-one sessions with industry mentors',
  icon: IdeaIcon,
      color: 'secondary'
    },
    {
      day: 'Day 2',
      date: 'December 12, 2025',
      time: '6:00 PM',
      title: 'Submission Deadline',
      description: 'All projects must be submitted by this time',
  icon: CodeIcon,
      color: 'error'
    },
    {
      day: 'Day 3',
      date: 'December 13, 2025',
      time: '10:00 AM',
      title: 'Project Presentations',
      description: 'Teams present their solutions to judges',
  icon: PresentIcon,
      color: 'primary'
    },
    {
      day: 'Day 3',
      date: 'December 13, 2025',
      time: '4:00 PM',
      title: 'Awards Ceremony',
      description: 'Winners announced, prizes awarded, closing remarks',
  icon: TrophyIcon,
      color: 'secondary'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <AnimatedSection animation="fadeIn">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Event Schedule
          </Typography>
          <Typography variant="h6" color="text.secondary">
            48 hours of innovation, collaboration, and impact
          </Typography>
        </Box>
      </AnimatedSection>

      <Timeline position="alternate">
        {scheduleData.map((item, index) => {
          return (
            <AnimatedSection key={index} animation="slideUp" delay={index * 0.05}>
              <TimelineItem>
                <TimelineOppositeContent
                  sx={{ m: 'auto 0' }}
                  align={index % 2 === 0 ? 'right' : 'left'}
                  variant="body2"
                  color="text.secondary"
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {item.day}
                  </Typography>
                  <Typography variant="body2">{item.date}</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, mt: 1 }}>
                    {item.time}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineConnector />
                  <TimelineDot
                    variant="filled"
                    sx={(theme) => {
                      const map = {
                        primary: theme.palette.primary.main,
                        secondary: theme.palette.secondary.main,
                        success: theme.palette.success.main,
                        warning: theme.palette.warning.main,
                        info: theme.palette.info.main,
                        error: theme.palette.error.main,
                        grey: theme.palette.grey[700]
                      };
                      const bg = map[item.color] || theme.palette.primary.main;
                      const fg = theme.palette.getContrastText(bg);
                      return {
                        p: 1.5,
                        bgcolor: bg,
                        color: fg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 0
                      };
                    }}
                  >
                    {(() => {
                      const IconComponent = item.icon;
                      return (
                        <IconComponent
                          htmlColor="currentColor"
                          sx={{ fontSize: 24, color: 'inherit', fill: 'currentColor' }}
                          aria-hidden
                        />
                      );
                    })()}
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Paper
                    elevation={3}
                    sx={{
                      p: 2,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        transition: 'all 0.3s ease',
                        boxShadow: 6
                      }
                    }}
                  >
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                      {item.title}
                    </Typography>
                    <Typography color="text.secondary">
                      {item.description}
                    </Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            </AnimatedSection>
          );
        })}
      </Timeline>

      <AnimatedSection animation="fadeIn" delay={0.5}>
        <Box sx={(theme) => ({
          mt: 6,
          p: 3,
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'grey.50',
          borderRadius: 2,
          textAlign: 'center',
          border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none'
        })}>
          <Typography variant="body1" color={ (theme) => theme.palette.mode === 'dark' ? 'text.secondary' : 'text.secondary' }>
            * Schedule is subject to minor adjustments. All participants will receive a detailed schedule closer to the event date.
          </Typography>
        </Box>
      </AnimatedSection>
    </Container>
  );
}

export default ScheduleTimeline;
